import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GetStaticComponentProps } from '@sitecore-jss/sitecore-jss-nextjs';
import {
  ComponentRendering,
  LayoutServiceData,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-react';
import { sanitize } from 'isomorphic-dompurify';
import debounce from 'lodash-es/debounce';
import { useRouter } from 'next/router';
import { GetStaticPropsContext } from 'next/types';

import {
  isNotNil,
  useFallbackTranslation,
  useLoginType,
  useSiteSettings,
} from '@gn-audio/jss-components';
import { DEFAULT_CHINA_LOCALE, DEFAULT_LOCALE, getLocaleWithRegion } from '@gn-audio/jss-core';

import { fetchColorHexMappings } from 'api/colorHexMappingApi';
import { SearchParameters } from 'api/productCatalogueApi/productCatalogueApiTypes';
import { fetchSfccPromoFilter } from 'api/sfccApi/fetchSfccPromoFilter';
import { ISfccSearchRefinement } from 'api/sfccApi/sfccApiTypes';
import { ProductEntityType } from 'common/types/productTypes';
import { QueryInclusionEnum } from 'common/types/queryInclusionType';
import { ProductFilterItem } from 'composables/Product/ProductFilterModule/ProductFilterItem';
import useInfiniteScroll from 'utils/hooks/useInfiniteScroll';

import {
  buildProductODatagQuery,
  buildSfccSearchQuery,
  buildUrlQueryParams,
  fetchCatalogProductDetails,
  fetchSfccProductDetails,
  parseUrlQueryParams,
} from './utils/productFilterModuleUtils';
import {
  CombinedResults,
  Locales,
  ProductFilterModuleProps,
  SelectedProductFilters,
} from './productFilterModuleTypes';
import ProductFilterSidebar from './ProductFilterSidebar';

const ProductFilterModuleSpot: FC<ProductFilterModuleProps> = ({
  fields,
  colorHexMappings,
  pimSfccMappings,
}) => {
  const {
    NumberOfResultsPerPage,
    PreFilterQuery,
    EntityType,
    NoResultsText,
    OrderableOnly,
    CountryOnlineFlag,
    loginType,
    campaignId,
    guestDeniedMessage,
  } = fields || {};
  const { asPath, query } = useRouter();
  const { sitecoreContext } = useSitecoreContext();
  const [loading, setLoading] = useState<boolean>(false);
  const { ecom, sfcc } = useSiteSettings();
  const { isVisible } = useLoginType(loginType);

  const locale = useMemo((): Locales => {
    return {
      sfccLocale: sfcc?.shopApiLocale || '',
      productCatalogueLocale: getLocaleWithRegion(sitecoreContext.language),
    };
  }, [sfcc?.shopApiLocale, sitecoreContext.language]);

  const sfccPromoFilterUrl = `${sfcc?.sfccBaseUrl}/${locale.sfccLocale.replace(/-/g, '_')}/Campaign-FilterValue?campaignId=${campaignId?.value}`;
  const resultsPerPage = NumberOfResultsPerPage?.value || 10;

  const [productFilters, setProductFilters] = useState<SelectedProductFilters>({
    FilterGroups: [],
    SelectedColor: [],
  });

  const sfccPromoFilterRef = useRef<string>('');
  const areInitialFiltersSet = useRef(false);

  useEffect(() => {
    if (Object.keys(query).length && !areInitialFiltersSet.current && fields) {
      const parsedFiltersFromUrl = parseUrlQueryParams(asPath, fields);
      setProductFilters(parsedFiltersFromUrl);
      areInitialFiltersSet.current = true;
    }
  }, [query, asPath, fields]);

  const basePath = asPath.split('?')[0];

  const [products, setProducts] = useState<CombinedResults[]>([]);
  const [searchRefinements, setSearchRefinements] = useState<ISfccSearchRefinement[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const translations = useFallbackTranslation();

  const productFilterItemsRef = useRef<HTMLDivElement>(null);

  const { doneFetching } = useInfiniteScroll({
    setPage,
    hasMore,
  });

  const buildCatalogPayload = useCallback(
    (take: number, skip: number): SearchParameters => {
      return {
        filter: buildProductODatagQuery(productFilters.FilterGroups, PreFilterQuery?.value),
        includePrices: true,
        marketLocale: locale.productCatalogueLocale,
        orderBy: productFilters.Order?.value,
        search: productFilters.SearchText,
        skip,
        top: take,
      };
    },
    [PreFilterQuery?.value, locale, productFilters]
  );

  const fetchCatalogSearchData = useCallback(
    async (searchParams: SearchParameters) => {
      return fetchCatalogProductDetails(searchParams, EntityType?.value || ProductEntityType.Group);
    },
    [EntityType?.value]
  );

  const buildSfccQuery = useCallback(
    (take: number, skip: number) =>
      buildSfccSearchQuery(
        productFilters,
        locale.sfccLocale,
        pimSfccMappings,
        OrderableOnly?.value || QueryInclusionEnum.Exclude,
        CountryOnlineFlag?.value || QueryInclusionEnum.Exclude,
        PreFilterQuery?.value,
        sfccPromoFilterRef.current,
        take,
        skip
      ),
    [
      PreFilterQuery?.value,
      locale.sfccLocale,
      productFilters,
      pimSfccMappings,
      OrderableOnly?.value,
      CountryOnlineFlag?.value,
    ]
  );

  const fetchSfccSearchData = useCallback(
    async (query: string) => {
      const sfccApiSettings = {
        baseUrl: sfcc?.shopApiBaseUrl ?? '',
        clientId: sfcc?.shopApiClientId ?? '',
      };

      return fetchSfccProductDetails({
        sfccApiSettings,
        sfccSearchQuery: query,
        locale: locale.productCatalogueLocale,
      });
    },
    [sfcc?.shopApiBaseUrl, sfcc?.shopApiClientId, locale]
  );

  const fetchProducts = async (
    queryBuilder: (take: number, skip: number) => string | SearchParameters,
    fetcher: (
      query: string | SearchParameters
    ) => Promise<{ refinements: ISfccSearchRefinement[]; combinedResults: CombinedResults[] }>,
    take: number,
    skip: number
  ) => {
    const query = queryBuilder(take, skip);
    const { refinements, combinedResults } = await fetcher(query);
    return { refinements, combinedResults };
  };

  const loadProducts = useCallback(
    async (page: number) => {
      if (!hasMore) {
        doneFetching();
        return;
      }

      const startingPage = page;
      const skip = (startingPage - 1) * resultsPerPage;
      const take = resultsPerPage;

      try {
        if (startingPage === 1) {
          setProducts([]);
        }
        setLoading(true);

        if (ecom?.ecomEnabled && campaignId?.value) {
          const sfccPromoFilterData = await fetchSfccPromoFilter(sfccPromoFilterUrl);
          sfccPromoFilterRef.current = sfccPromoFilterData?.promotionFilterValue || '';
        }

        const { combinedResults, refinements } = ecom?.ecomEnabled
          ? await fetchProducts(buildSfccQuery, fetchSfccSearchData, take, skip)
          : await fetchProducts(buildCatalogPayload, fetchCatalogSearchData, take, skip);

        if (combinedResults.length === 0) setHasMore(false);
        if (refinements.length) setSearchRefinements(refinements);

        setProducts((prevProducts) =>
          startingPage === 1 ? combinedResults : [...prevProducts, ...combinedResults]
        );
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        doneFetching();
        setLoading(false);
      }
    },
    [
      hasMore,
      resultsPerPage,
      doneFetching,
      ecom?.ecomEnabled,
      campaignId?.value,
      buildSfccQuery,
      fetchSfccSearchData,
      buildCatalogPayload,
      fetchCatalogSearchData,
      sfccPromoFilterUrl,
    ]
  );

  const urlQueryParams = buildUrlQueryParams(productFilters);

  useEffect(() => {
    const loadDebounced = debounce(loadProducts, 50);
    loadDebounced(page);
    return () => {
      loadDebounced.cancel();
    };
  }, [page, urlQueryParams, locale, basePath, loadProducts]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    setHasMore(true);

    // we are not using router replace here to avoid whole page rerender
    const newBasePath =
      locale.productCatalogueLocale !== DEFAULT_LOCALE &&
      locale.productCatalogueLocale.toLowerCase() !== DEFAULT_CHINA_LOCALE.toLowerCase()
        ? `/${locale.productCatalogueLocale}${basePath}`
        : basePath;
    const newUrl = `${newBasePath}?${urlQueryParams}`;
    window.history.replaceState({}, '', newUrl);

    if (productFilterItemsRef.current) productFilterItemsRef.current.scrollTop = 0;
    setPage(1);
  }, [urlQueryParams, locale, basePath]);

  if (!isVisible)
    return (
      <div className="container-fluid flex justify-center px-4 min-[1921px]:px-0">
        {guestDeniedMessage?.value}
      </div>
    );

  const hasNoResults = !loading && !products.length && !hasMore;

  return (
    <>
      <div className="container-fluid flex flex-col px-4 lg:flex-row min-[1921px]:px-0">
        <div className="h-[80%] w-full lg:w-1/4">
          {fields && (
            <ProductFilterSidebar
              searchRefinements={searchRefinements}
              options={fields}
              productFilters={productFilters}
              colorHexMappings={colorHexMappings}
              setProductFilters={setProductFilters}
            />
          )}
        </div>

        {!hasNoResults && (
          <div className="w-full pb-2 pt-3 lg:w-4/5 lg:pl-4 lg:pt-0" ref={productFilterItemsRef}>
            <div className="grid grid-cols-1 items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {products
                .filter(
                  (
                    product
                  ): product is {
                    productCatalogueLabels: NonNullable<CombinedResults['productCatalogueLabels']>;
                  } => isNotNil(product.productCatalogueLabels)
                )
                .map((product) => (
                  <div key={product.productCatalogueLabels.sku}>
                    <ProductFilterItem translations={translations} product={product} />
                  </div>
                ))}
              {loading &&
                Array.from({ length: resultsPerPage }).map((_, index) => (
                  <div
                    className="light flex h-[640px] flex-col rounded-lg border-gray-100 bg-white shadow-md"
                    key={index}
                  >
                    <div className="animate-glow group relative flex h-[350px] justify-center rounded-t-lg bg-gray-90"></div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {hasNoResults && (
          <div
            className="flex w-full items-center justify-center py-8 lg:pt-0"
            dangerouslySetInnerHTML={{
              __html: sanitize(NoResultsText?.value || ''),
            }}
          ></div>
        )}
      </div>
    </>
  );
};

export const getStaticProps: GetStaticComponentProps = async (
  _: ComponentRendering,
  __: LayoutServiceData,
  context: GetStaticPropsContext
) => {
  const colorHexMappings = await fetchColorHexMappings(context.locale);
  return {
    colorHexMappings,
  };
};

export default ProductFilterModuleSpot;
